import React, { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PartnerDto,
  CreatePartnerRequest,
  UpdatePartnerRequest,
} from "@/types/partner";
import { partnerService } from "@/services/partnerService";
import { toast } from "@/hooks/use-toast";

interface PartnerFormDialogProps {
  partner?: PartnerDto | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PartnerFormDialog: React.FC<PartnerFormDialogProps> = ({
  partner,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<PartnerDto>>({
    referralCode: "",
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    address: "",
    referralPercentage: 0,
    status: "ACTIVE",
    taxId: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (partner) {
      setFormData(partner);
    } else {
      // Reset form for new partner
      setFormData({
        referralCode: "",
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
        address: "",
        referralPercentage: 0,
        status: "ACTIVE",
        taxId: "",
        notes: "",
      });
    }
  }, [partner, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (partner?.id) {
        // Update existing partner
        const updateData: UpdatePartnerRequest = {
          ...(formData as PartnerDto),
          id: partner.id,
        };
        await partnerService.updatePartner(partner.id, updateData);
        toast({
          title: "Success",
          description: "Partner updated successfully",
        });
      } else {
        const createData: CreatePartnerRequest =
          formData as CreatePartnerRequest;
        await partnerService.createPartner(createData);
        toast({
          title: "Success",
          description: "Partner created successfully",
        });
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving partner:", error);
      toast({
        title: "Error",
        description: "Failed to save partner",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: keyof PartnerDto,
    value: string | number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {partner ? "Edit Partner" : "Create New Partner"}
          </DialogTitle>
          <DialogDescription>
            {partner
              ? "Update the partner information below."
              : "Enter the information for the new partner."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="referralCode">Referral Code *</Label>
              <Input
                id="referralCode"
                value={formData.referralCode || ""}
                onChange={(e) =>
                  handleInputChange("referralCode", e.target.value)
                }
                placeholder="e.g., PARTNER001"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: "ACTIVE" | "INACTIVE") =>
                  handleInputChange("status", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={formData.companyName || ""}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
              placeholder="Company name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name *</Label>
              <Input
                id="contactName"
                value={formData.contactName || ""}
                onChange={(e) =>
                  handleInputChange("contactName", e.target.value)
                }
                placeholder="Contact person name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="contact@company.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <InputMask
                mask="(999) 999-9999"
                maskChar="_"
                value={formData.phone || ""}
                onChange={(e) => handleInputChange("phone", e.target.value)}
              >
                {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                  <Input
                    {...inputProps}
                    id="phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                  />
                )}
              </InputMask>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referralPercentage">Referral Percentage *</Label>
              <div className="flex items-center border rounded-md px-3 shadow-sm">
                <Input
                  id="referralPercentage"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.referralPercentage || 0}
                  onChange={(e) =>
                    handleInputChange(
                      "referralPercentage",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  placeholder="0"
                  className="border-none focus-visible:ring-0 focus-visible:ring-offset-0 pr-1"
                  required
                />
                <span className="text-muted-foreground">%</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes || ""}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Additional notes about this partner"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting
                ? "Saving..."
                : partner
                  ? "Update Partner"
                  : "Create Partner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerFormDialog;
